import { IService, ServiceEvent } from '../base/BaseService';

/**
 * Error codes for the AudioService
 */
export enum AudioErrorCode {
  LOAD_FAILED = 'load_failed',
  PLAYBACK_FAILED = 'playback_failed',
  INVALID_TIME = 'invalid_time',
  INVALID_URL = 'invalid_url',
  NOT_LOADED = 'not_loaded',
  BROWSER_UNSUPPORTED = 'browser_unsupported'
}

/**
 * Playback rates supported by the audio service
 */
export enum PlaybackRate {
  VERY_SLOW = 0.5,
  SLOW = 0.75,
  NORMAL = 1.0,
  FAST = 1.25,
  VERY_FAST = 1.5,
  SUPER_FAST = 2.0
}

/**
 * Audio playback state enum
 */
export enum AudioState {
  STOPPED = 'stopped',
  PLAYING = 'playing',
  PAUSED = 'paused',
  LOADING = 'loading',
  ERROR = 'error'
}

/**
 * Events emitted by the AudioService
 */
export enum AudioEvent {
  TIME_UPDATE = 'time_update',
  STATE_CHANGE = 'state_change',
  DURATION_CHANGE = 'duration_change',
  VOLUME_CHANGE = 'volume_change',
  LOADING = 'loading',
  ERROR = 'error',
  WAVEFORM_DATA_LOADED = 'waveform_data_loaded',
  // Legacy events for test compatibility
  AUDIO_LOADED = 'audio_loaded',
  PLAYBACK_STARTED = 'playback_started',
  PLAYBACK_PAUSED = 'playback_paused'
}

/**
 * Make AudioEvent compatible with ServiceEvent
 */
// Enum extends implicitly

/**
 * Custom error class for AudioService
 */
export class AudioServiceError extends Error {
  constructor(message: string, public readonly code: AudioErrorCode) {
    super(message);
    this.name = 'AudioServiceError';
  }
}

/**
 * Legacy interface for AudioService observers
 */
export interface AudioServiceObserver {
  onAudioEvent(eventType: AudioEvent, payload?: any): void;
}

/**
 * Interface for the audio service
 */
export interface IAudioService extends IService<AudioEvent> {
  /**
   * Loads an audio file
   */
  loadAudio(url: string): Promise<boolean>;
  
  /**
   * Loads waveform data for visualization
   */
  loadWaveformData(url: string | number[]): Promise<boolean>;
  
  /**
   * Starts or resumes audio playback
   */
  play(): boolean;
  
  /**
   * Pauses audio playback
   */
  pause(): boolean;
  
  /**
   * Toggles play/pause state
   */
  togglePlayPause(): boolean;
  
  /**
   * Seeks to a specific time in the audio
   */
  seekTo(time: number): boolean;
  
  /**
   * Sets the playback rate
   */
  setPlaybackRate(rate: PlaybackRate): boolean;
  
  /**
   * Sets the audio volume
   */
  setVolume(volume: number): boolean;
  
  /**
   * Mutes or unmutes the audio
   */
  setMuted(muted: boolean): boolean;
  
  /**
   * Legacy method to register an observer
   */
  registerObserver(observer: AudioServiceObserver): void;
  
  /**
   * Legacy method to unregister an observer
   */
  unregisterObserver(observer: AudioServiceObserver): void;
  
  /**
   * Get methods
   */
  getState(): AudioState;
  getCurrentTime(): number;
  getDuration(): number;
  getVolume(): number;
  getWaveformData(): number[] | null;
  isLoading(): boolean;
  
  /**
   * Observables (for MobX)
   */
  readonly isLoaded: boolean;
  readonly state: AudioState;
  readonly isPlaying: boolean;
  readonly currentTime: number;
  readonly duration: number;
  readonly playbackRate: PlaybackRate;
  readonly volume: number;
  readonly muted: boolean;
  readonly audioUrl: string | null;
  readonly waveformData: number[] | null;
}